import React from 'react';
import { ToolResultField, FieldValue } from './ToolResultField';
import { List, CheckCircle } from 'lucide-react';

interface ToolResultItem {
  values: FieldValue[];
}

interface ToolResultListProps {
  items: ToolResultItem[];
  tool_name: string;
  action_name: string;
  onLinkClick?: (href: string) => void;
}

const formatActionTitle = (name: string): string => {
  const safeName = (name ?? 'Action').toString();
  return safeName.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
};

/**
 * Renders multiple tool execution results as a grid of cards
 */
export const ToolResultList: React.FC<ToolResultListProps> = ({
  items,
  tool_name,
  action_name,
  onLinkClick
}) => {
  if (!items || items.length === 0) {
    return (
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-center">
        <div className="text-4xl mb-2">📭</div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Results</h3>
        <p className="text-gray-600">No items found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center gap-2 px-1">
        <CheckCircle className="w-4 h-4 text-green-600" />
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-gray-900">{formatActionTitle(action_name)}</h3>
          <p className="text-xs text-gray-500">
            {items.length} {items.length === 1 ? 'result' : 'results'} from {tool_name}
          </p>
        </div>
      </div>

      {/* Results Grid */}
      <div className="grid grid-cols-1 gap-3">
        {items.map((item, index) => (
          <div
            key={index}
            className="w-full bg-white border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-sm transition-all"
          >
            <div className="space-y-1">
              {item.values.map((field, fieldIndex) => (
                <ToolResultField
                  key={`${index}-${field.path}-${fieldIndex}`}
                  field={field}
                  onLinkClick={onLinkClick}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Footer summary */}
      {items.length > 3 && (
        <div className="text-center text-xs text-gray-500 pt-2">
          Showing all {items.length} results
        </div>
      )}
    </div>
  );
};
